/**
 * Focus bookkeeping shared by everything that behaves like a dialog.
 *
 * The sidebar drawer and `Modal` need the same three things — which controls a
 * Tab press can actually reach, how to take focus on open, and where to put it
 * back on close — and had no business each inventing its own answer.
 */

/**
 * Whether a control can actually take focus.
 *
 * Deliberately asks the DOM rather than the layout. `offsetParent` is the usual
 * shorthand for "is this on screen", and it is wrong twice here: it is null for
 * anything inside a `position: fixed` subtree — which the drawer is — and null
 * for everything under jsdom, where there is no layout at all, so the check
 * would quietly empty the list rather than filter it.
 */
export function isReachable(element: HTMLElement): boolean {
  if (element.tabIndex < 0) return false;
  if (element.closest("[hidden], [inert], [aria-hidden='true']")) return false;
  const style = typeof getComputedStyle === "function" ? getComputedStyle(element) : null;
  return !style || (style.display !== "none" && style.visibility !== "hidden");
}

/**
 * The elements inside `root` a Tab press can reach, in tab order.
 *
 * Disabled and hidden controls are excluded: a disabled field still answers a
 * naive `input, select, textarea` selector but can never hold focus, so
 * counting it as the first or last stop sends the wrap to a dead end.
 */
export function focusableWithin(root: HTMLElement): HTMLElement[] {
  const candidates = root.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
      'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  return Array.from(candidates).filter(isReachable);
}

/**
 * Marks a layer that must stay reachable even underneath a modal.
 *
 * Toasts are the case that matters: a save can fail while its dialog stays
 * deliberately open, and the notification about that failure — with its retry
 * and dismiss buttons — must not be inert. It is announced and operable, or it
 * may as well not be shown.
 */
export const PERSISTENT_LAYER_ATTR = "data-persistent-layer";

/**
 * How many open dialogs are relying on a given element being inert.
 *
 * A plain "skip anything already inert" rule quietly assumed dialogs close in
 * the order they opened. Counting instead means the page comes back exactly
 * when the last dialog over it goes, whatever order they unmount in.
 */
const inertHolds = new WeakMap<HTMLElement, number>();

/**
 * Takes the rest of the page out of the tree while a dialog is over it, and
 * returns the undo.
 *
 * `inert` is the part a hand-rolled Tab handler cannot do: it removes a subtree
 * from focus order, from hit testing, and from the accessibility tree in one
 * step. Without it, `aria-modal` is a claim the page does not honour — the
 * dimmed content behind still answered the keyboard and a screen reader.
 */
export function inertSiblings(dialog: HTMLElement, keep: (element: HTMLElement) => boolean = () => false): () => void {
  const held: HTMLElement[] = [];
  for (const sibling of dialog.parentElement?.children ?? []) {
    if (!(sibling instanceof HTMLElement) || sibling === dialog) continue;
    if (sibling.hasAttribute(PERSISTENT_LAYER_ATTR) || keep(sibling)) continue;

    const holds = inertHolds.get(sibling) ?? 0;
    inertHolds.set(sibling, holds + 1);
    sibling.setAttribute("inert", "");
    held.push(sibling);
  }

  return () => {
    for (const sibling of held) {
      const holds = (inertHolds.get(sibling) ?? 1) - 1;
      if (holds > 0) {
        inertHolds.set(sibling, holds);
        continue;
      }
      inertHolds.delete(sibling);
      sibling.removeAttribute("inert");
    }
  };
}

/**
 * The open dialogs, innermost last.
 *
 * Every modal used to install its own window-level key handler, so an outer
 * dialog that stayed mounted behind an inner one still answered the keyboard:
 * Escape closed both — cancelling a delete confirmation also threw away the
 * editor and its unsaved draft — and Tab was handled by the outer dialog first,
 * which then tried to move focus into its own inert contents and stuck.
 */
const layers: symbol[] = [];

export interface ModalLayer {
  /** Whether this dialog is the one the keyboard is talking to. */
  isTop: () => boolean;
  release: () => void;
}

export function enterModalLayer(): ModalLayer {
  const id = Symbol("modal-layer");
  layers.push(id);
  return {
    isTop: () => layers[layers.length - 1] === id,
    release: () => {
      const at = layers.lastIndexOf(id);
      if (at !== -1) layers.splice(at, 1);
    },
  };
}

/** Moves focus into a dialog: its first control, or the container itself. */
export function focusInto(dialog: HTMLElement): void {
  const first = focusableWithin(dialog)[0];
  (first ?? dialog).focus();
}

/**
 * Everything the keyboard may reach while `dialog` is the active layer: the
 * dialog itself, plus any persistent layer over it.
 *
 * Exempting toasts from `inert` and raising them above the overlay made them
 * clickable, and no more than that. Tab was still confined to the dialog, so a
 * "your session expired — Sign in" toast raised while a modal is open was
 * visible, operable by mouse, and unreachable by keyboard: the two buttons that
 * matter sat outside the only scope Tab could move within.
 */
function tabScope(dialog: HTMLElement): HTMLElement[] {
  const scope = focusableWithin(dialog);
  for (const layer of document.querySelectorAll<HTMLElement>(`[${PERSISTENT_LAYER_ATTR}]`)) {
    if (layer === dialog || layer.contains(dialog)) continue;
    scope.push(...focusableWithin(layer));
  }
  return scope;
}

/**
 * Contains Tab within `dialog`, for a keydown event.
 *
 * A backstop for where `inert` is unsupported, and for the two cases a
 * boundary-only check cannot see: focus resting on the dialog container itself
 * (Shift+Tab from there walks out to whatever precedes it in the document), and
 * focus placed behind the dialog programmatically.
 */
export function containTab(dialog: HTMLElement, event: KeyboardEvent): void {
  const focusable = tabScope(dialog);
  if (focusable.length === 0) {
    event.preventDefault();
    dialog.focus();
    return;
  }

  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  const active = document.activeElement as HTMLElement | null;
  const index = active ? focusable.indexOf(active) : -1;

  if (index === -1) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }

  // Drives the whole traversal rather than only correcting at the two ends.
  // The scope can span detached subtrees -- a dialog portalled to <body> and a
  // toast layer beside it -- whose document order has nothing to do with the
  // order a reader should move through them. Left to the browser between the
  // boundaries, Tab stepped out of the dialog into whatever happened to come
  // next in the document.
  event.preventDefault();
  const step = event.shiftKey ? -1 : 1;
  const next = (index + step + focusable.length) % focusable.length;
  focusable[next]!.focus();
}

/**
 * Hands focus back to whatever opened the dialog.
 *
 * Focusing a detached node silently does nothing, which leaves focus stranded
 * on an element no longer in the document: the next Tab starts again from the
 * top of the page. A dialog's opener genuinely can be gone by the time this
 * runs — the sidebar's own contents unmount as it collapses.
 *
 * `fallbackRoot` is where focus goes instead, and should be whatever remains
 * visible where the dialog was, so the keyboard can carry on from there.
 */
export function restoreFocus(target: HTMLElement | null, fallbackRoot?: HTMLElement | null): void {
  if (target?.isConnected && isReachable(target)) {
    target.focus();
    if (document.activeElement === target) return;
  }

  const fallback = fallbackRoot?.isConnected ? focusableWithin(fallbackRoot)[0] : undefined;
  if (fallback) {
    fallback.focus();
    if (document.activeElement === fallback) return;
  }

  (document.activeElement as HTMLElement | null)?.blur();
  document.body.focus();
}
